import { errorMessage, titleColorHex } from "@atlas/shared";
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Link } from "@tanstack/react-router";
import type { User } from "@atlas/shared";
import {
  LogOut,
  Trash2,
  RotateCcw,
  BarChart3,
  Plug,
  CreditCard,
  ChevronRight,
  Camera,
  ShieldCheck,
  Sparkles,
  X,
  Check,
  Award,
  Image as ImageIcon,
  MountainSnow,
  Link2,
} from "lucide-react";
import { logout } from "../api/auth";
import { fetchBillingStatus } from "../api/billing";
import { fetchProfileStats, updateThemePreference } from "../api/profile";
import { patchAccount } from "../api/social";
import { getInventory, equipItems, type EquipSlot, type InventoryItem } from "../api/store";
import { apiDelete, apiPatch, apiPost } from "../lib/api-client";
import { Button } from "../components/Button";
import { ShareCardButton } from "../components/ShareCardButton";
import { fetchShowcaseUrl } from "../api/showcase";
import { AtlasPanel } from "../components/AtlasPanel";
import { AnnouncementsPanel } from "../components/AnnouncementsPanel";
import { EmailPreferencesPanel } from "../components/EmailPreferencesPanel";
import { EmailCaptureCard } from "../components/EmailCaptureCard";
import { DataExportPanel } from "../components/DataExportPanel";
import { InvitesPanel } from "../components/InvitesPanel";
import { SignInMethodsPanel } from "../components/SignInMethodsPanel";
import { ProfileIdentity } from "../components/ProfileIdentity";
import { getBadgeAsset, getBannerAsset, getFrameAsset, getStampAsset } from "../lib/cosmetics";
import { FREE_THEMES, STORE_THEME_SKINS, applySiteTheme, setBaseTheme, storedBaseTheme } from "../lib/theme";
import { rarityColor, rarityGlowMd, rarityGlowSm, rarityLabel } from "../lib/rarity";
import { useAuth } from "../hooks/useAuth";

const USERNAME_RE = /^[a-z0-9-]{3,24}$/;

// ─── Avatar ───────────────────────────────────────────────────────────────────

function ProfileAvatar({
  name,
  avatarUrl,
  size = 72,
  onUploadClick,
  uploading,
}: {
  name: string;
  avatarUrl?: string | null;
  size?: number;
  onUploadClick?: () => void;
  uploading?: boolean;
}) {
  const initials = name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const fontSize = Math.round(size * 0.38);

  return (
    <div style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      <div
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          overflow: "hidden",
          border: "3px solid var(--accent)",
          boxShadow: "0 0 0 2px var(--bg-app), var(--glow-gold-sm)",
          background: "var(--surface-raised)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize,
          color: "var(--text-accent)",
        }}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          initials || "?"
        )}
      </div>
      {onUploadClick && (
        <button
          onClick={onUploadClick}
          disabled={uploading}
          aria-label="Change photo"
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: "var(--accent)",
            border: "2px solid var(--bg-app)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: uploading ? "not-allowed" : "pointer",
            opacity: uploading ? 0.6 : 1,
          }}
        >
          <Camera size={13} color="var(--text-on-gold)" />
        </button>
      )}
    </div>
  );
}

// ─── Delete Account Dialog ────────────────────────────────────────────────────

function DeleteAccountDialog({ onClose }: { onClose: () => void }) {
  const [confirmed, setConfirmed] = React.useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiDelete<{ ok: boolean; message: string }>("/account/me", { confirm: "DELETE" }),
    onSuccess: async () => {
      queryClient.clear();
      await navigate({ to: "/login" });
    },
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 28,
          maxWidth: 400,
          width: "90%",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 8 }}>
          Delete your account?
        </h2>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 20, lineHeight: 1.5 }}>
          This will permanently delete all your activity data, peak logs,
          achievements, and progress. This cannot be undone.
        </p>

        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 20 }}>
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            style={{ width: 18, height: 18, accentColor: "var(--status-danger)" }}
          />
          <span style={{ fontSize: 14, color: "var(--text-primary)" }}>
            I understand this is permanent
          </span>
        </label>

        {deleteMutation.isError && (
          <p style={{ fontSize: 13, color: "var(--status-danger)", marginBottom: 12 }}>
            {errorMessage(deleteMutation.error, "Failed to delete account")}
          </p>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={!confirmed || deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete account"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Reset Account Dialog ─────────────────────────────────────────────────────

const RESET_PHRASE = "reset my account";

function ResetAccountDialog({ onClose }: { onClose: () => void }) {
  const [phrase, setPhrase] = React.useState("");
  const queryClient = useQueryClient();

  const resetMutation = useMutation({
    mutationFn: () =>
      apiPost<{ ok: boolean; message: string }>("/account/reset", { confirm: phrase }),
    onSuccess: async () => {
      // Everything the app has cached is now stale — refetch from a clean slate.
      await queryClient.invalidateQueries();
      onClose();
    },
  });

  const phraseMatches = phrase.trim().toLowerCase() === RESET_PHRASE;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 28,
          maxWidth: 440,
          width: "90%",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 8 }}>
          Reset your account?
        </h2>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 16, lineHeight: 1.5 }}>
          This permanently removes all your activities, achievements, waypoints,
          Outdoor Score, collections, goals and posts. Your plan, any lifetime
          purchase, and connected sources are kept. This cannot be undone.
        </p>

        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>
          Type <span style={{ color: "var(--text-primary)" }}>{RESET_PHRASE}</span> to confirm
        </label>
        <input
          type="text"
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          autoFocus
          autoComplete="off"
          placeholder={RESET_PHRASE}
          style={{
            width: "100%",
            height: "var(--control-md)",
            padding: "0 12px",
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-md)",
            color: "var(--text-primary)",
            fontSize: "var(--text-base)",
            fontFamily: "var(--font-sans)",
            outline: "none",
            boxSizing: "border-box",
            marginBottom: 20,
          }}
        />

        {resetMutation.isError && (
          <p style={{ fontSize: 13, color: "var(--status-danger)", marginBottom: 12 }}>
            {errorMessage(resetMutation.error, "Failed to reset account")}
          </p>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={!phraseMatches || resetMutation.isPending}
            onClick={() => resetMutation.mutate()}
          >
            {resetMutation.isPending ? "Resetting…" : "Reset account"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Email verification banner ────────────────────────────────────────────────

// ─── Security panel: change / set password ────────────────────────────────────

function SecurityPanel({ hasPassword }: { hasPassword: boolean }) {
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [mismatch, setMismatch] = React.useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      apiPatch<{ user: unknown }>("/account/me", {
        ...(hasPassword ? { currentPassword } : {}),
        newPassword,
      }),
    onSuccess: async () => {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword.length < 8) {
      setMismatch(false);
      return;
    }
    if (newPassword !== confirmPassword) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    mutation.mutate();
  }

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: "var(--control-md)",
    padding: "0 12px",
    background: "var(--surface-sunken)",
    border: "1px solid var(--border-default)",
    borderRadius: "var(--radius-md)",
    color: "var(--text-primary)",
    fontSize: "var(--text-base)",
    fontFamily: "var(--font-sans)",
    outline: "none",
    boxSizing: "border-box",
  };

  return (
    <AtlasPanel title="Security">
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 400 }}>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: 0 }}>
          {hasPassword
            ? "Change your password."
            : "Your account signed in with Strava. Set a password to also sign in with email."}
        </p>

        {hasPassword && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>
              Current password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
              style={inputStyle}
            />
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>
            {hasPassword ? "New password" : "Password"}
          </label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            style={inputStyle}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>
            Confirm password
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            style={inputStyle}
          />
        </div>

        {mismatch && (
          <div style={{ fontSize: 13, color: "var(--status-danger)" }}>
            Passwords do not match
          </div>
        )}
        {mutation.isError && (
          <div style={{ fontSize: 13, color: "var(--status-danger)" }}>
            {errorMessage(mutation.error, "Failed to update password")}
          </div>
        )}
        {mutation.isSuccess && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--spruce-300)" }}>
            <ShieldCheck size={14} />
            Password updated
          </div>
        )}

        <div>
          <Button type="submit" size="sm" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : hasPassword ? "Update password" : "Set password"}
          </Button>
        </div>
      </form>
    </AtlasPanel>
  );
}

// ─── Public profile panel (SOC-1 username/visibility + SOC-2 auto-post) ───────

function PublicProfilePanel({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const [usernameDraft, setUsernameDraft] = React.useState(user.username ?? "");
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    setUsernameDraft(user.username ?? "");
  }, [user.username]);

  const normalized = usernameDraft.trim().toLowerCase();
  const usernameValid = USERNAME_RE.test(normalized);
  const usernameDirty = normalized !== (user.username ?? "");

  const invalidateMe = () => queryClient.invalidateQueries({ queryKey: ["me"] });

  const usernameMutation = useMutation({
    mutationFn: (username: string) => patchAccount({ username }),
    onSuccess: invalidateMe,
  });

  const visibilityMutation = useMutation({
    mutationFn: (profileVisibility: "public" | "private") => patchAccount({ profileVisibility }),
    onSuccess: invalidateMe,
  });

  const autoPostMutation = useMutation({
    mutationFn: (autoPostAchievements: boolean) => patchAccount({ socialPreferences: { autoPostAchievements } }),
    onSuccess: invalidateMe,
  });

  const isPublic = user.profileVisibility === "public";
  const profileUrl = user.username
    ? `${window.location.origin}/explorers/${user.username}`
    : null;

  function copyLink() {
    if (!profileUrl) return;
    void navigator.clipboard.writeText(profileUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const rowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
  };
  const toggleLabelStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    flexShrink: 0,
  };

  return (
    <AtlasPanel title="Public profile">
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {/* Username */}
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 6 }}>
            Username
          </label>
          <div style={{ display: "flex", gap: 10, maxWidth: 380 }}>
            <input
              value={usernameDraft}
              onChange={(e) => setUsernameDraft(e.target.value.toLowerCase())}
              placeholder="yourname"
              maxLength={24}
              style={{
                flex: 1,
                height: "var(--control-md)",
                padding: "0 12px",
                background: "var(--surface-sunken)",
                border: "1px solid var(--border-default)",
                borderRadius: "var(--radius-md)",
                color: "var(--text-primary)",
                fontSize: 14,
                fontFamily: "var(--font-mono)",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            <Button
              size="sm"
              disabled={!usernameValid || !usernameDirty || usernameMutation.isPending}
              onClick={() => usernameMutation.mutate(normalized)}
            >
              {usernameMutation.isPending ? "Saving…" : user.username ? "Update" : "Claim"}
            </Button>
          </div>
          {usernameDraft.length > 0 && !usernameValid && (
            <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6, marginBottom: 0 }}>
              3–24 characters: lowercase letters, numbers and hyphens only.
            </p>
          )}
          {usernameMutation.isError && (
            <p style={{ fontSize: 12, color: "var(--status-danger)", marginTop: 6, marginBottom: 0 }}>
              {errorMessage(usernameMutation.error, "Failed to update username")}
            </p>
          )}
          {usernameMutation.isSuccess && !usernameMutation.isPending && (
            <p style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--spruce-300)", marginTop: 6, marginBottom: 0 }}>
              <Check size={13} /> Username saved
            </p>
          )}
        </div>

        <div style={{ height: 1, background: "var(--border-subtle)" }} />

        {/* Visibility */}
        <div style={rowStyle}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Public profile</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2, maxWidth: 420, lineHeight: 1.5 }}>
              Public means anyone with the link sees your trophy case: stats, achievements and posts.
              Private (default) keeps it visible only to you.
            </div>
          </div>
          <label style={toggleLabelStyle}>
            <input
              type="checkbox"
              checked={isPublic}
              disabled={visibilityMutation.isPending || !user.username}
              onChange={(e) => visibilityMutation.mutate(e.target.checked ? "public" : "private")}
              style={{ width: 16, height: 16, accentColor: "var(--accent)" }}
            />
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{isPublic ? "Public" : "Private"}</span>
          </label>
        </div>

        {isPublic && profileUrl && user.username && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Link
              to="/explorers/$username"
              params={{ username: user.username }}
              style={{ fontSize: 13, color: "var(--sky-300)", textDecoration: "none" }}
            >
              View public profile
            </Link>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={copied ? <Check size={13} /> : <Link2 size={13} />}
              onClick={copyLink}
            >
              {copied ? "Copied" : "Copy link"}
            </Button>
          </div>
        )}

        <div style={{ height: 1, background: "var(--border-subtle)" }} />

        {/* Auto-post */}
        <div style={rowStyle}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Auto-post unlocks</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2, maxWidth: 420, lineHeight: 1.5 }}>
              Auto-post gold and platinum unlocks to the community feed.
            </div>
          </div>
          <label style={toggleLabelStyle}>
            <input
              type="checkbox"
              checked={!!user.socialPreferences?.autoPostAchievements}
              disabled={autoPostMutation.isPending}
              onChange={(e) => autoPostMutation.mutate(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: "var(--accent)" }}
            />
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {user.socialPreferences?.autoPostAchievements ? "On" : "Off"}
            </span>
          </label>
        </div>

        <div style={{ height: 1, background: "var(--border-subtle)" }} />

        {/* Showcase */}
        <div style={rowStyle}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Showcase</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2, maxWidth: 420, lineHeight: 1.5 }}>
              A full-screen map of everything you&#39;ve completed - anyone with the
              link can view it, and it always shows your latest record.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <ShareCardButton fetchUrl={fetchShowcaseUrl} label="Share showcase" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void fetchShowcaseUrl().then(({ url }) => window.location.assign(url));
              }}
            >
              Open
            </Button>
          </div>
        </div>
      </div>
    </AtlasPanel>
  );
}

// ─── Customise drawer (STO-4 equipping) ───────────────────────────────────────

type EquippedIds = {
  badgeItemIds: string[];
  bannerItemId: string | null;
  titleItemId: string | null;
  mapThemeItemId: string | null;
  frameItemId: string | null;
  traceStyleItemId: string | null;
  stampItemId: string | null;
  pinItemId: string | null;
};

type SingleSlot = "banner" | "title" | "mapTheme" | "frame" | "traceStyle" | "stamp" | "pin";

const SLOT_GROUPS: { slot: EquipSlot; kind: InventoryItem["kind"]; label: string; max: number }[] = [
  { slot: "badges", kind: "badge", label: "Badges", max: 3 },
  { slot: "banner", kind: "banner", label: "Banner", max: 1 },
  { slot: "title", kind: "title", label: "Title", max: 1 },
  { slot: "frame", kind: "frame", label: "Avatar frame", max: 1 },
  // Store themes re-skin the whole site (and keep their map heat ramp);
  // the drawer also offers the free base themes in this group.
  { slot: "mapTheme", kind: "map_theme", label: "Theme", max: 1 },
  { slot: "traceStyle", kind: "trace_style", label: "Trace style", max: 1 },
  { slot: "pin", kind: "pin", label: "Map pin", max: 1 },
  { slot: "stamp", kind: "stamp", label: "Check-in stamp", max: 1 },
];

function itemPreview(item: InventoryItem) {
  switch (item.kind) {
    case "badge": {
      const src = getBadgeAsset(item.metadata.assetKey);
      return src ? (
        <img src={src} alt="" width={32} height={32} style={{ borderRadius: "50%" }} />
      ) : (
        <Award size={22} style={{ color: "var(--gold-400)" }} />
      );
    }
    case "banner": {
      const src = getBannerAsset(item.metadata.assetKey);
      return src ? (
        <img
          src={src}
          alt=""
          style={{ width: 120, height: 28, objectFit: "cover", borderRadius: "var(--radius-sm)" }}
        />
      ) : (
        <ImageIcon size={20} style={{ color: "var(--text-faint)" }} />
      );
    }
    case "map_theme":
      return (
        <div style={{ display: "flex", height: 20, width: 56, borderRadius: "var(--radius-sm)", overflow: "hidden" }}>
          {(item.metadata.heatRamp ?? []).map((c, i) => (
            <span key={i} style={{ flex: 1, background: c }} />
          ))}
        </div>
      );
    case "frame": {
      const src = getFrameAsset(item.metadata.assetKey);
      return src ? (
        <img src={src} alt="" width={32} height={32} />
      ) : (
        <ImageIcon size={20} style={{ color: "var(--text-faint)" }} />
      );
    }
    case "stamp": {
      const src = getStampAsset(item.metadata.assetKey);
      return src ? (
        <img src={src} alt="" width={32} height={32} />
      ) : (
        <ImageIcon size={20} style={{ color: "var(--text-faint)" }} />
      );
    }
    case "trace_style":
      return (
        <svg width="56" height="20" viewBox="0 0 56 20" aria-hidden="true">
          {item.metadata.glow && (
            <path d="M4 14 C 18 4, 38 16, 52 6" stroke="var(--gold-400)" strokeWidth="7" fill="none" strokeLinecap="round" opacity="0.35" />
          )}
          <path
            d="M4 14 C 18 4, 38 16, 52 6"
            stroke="var(--gold-400)"
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={item.metadata.dashArray ? item.metadata.dashArray.map((n) => n * 3).join(" ") : undefined}
          />
        </svg>
      );
    case "pin":
      return (
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: item.metadata.color ?? "var(--gold-400)",
            border: "2px solid rgba(255,255,255,0.25)",
            display: "inline-block",
          }}
        />
      );
    default:
      return <MountainSnow size={20} style={{ color: "var(--text-faint)" }} />;
  }
}

function titleColor(item: InventoryItem): string {
  switch (item.metadata.colorToken) {
    case "sky":
      return "var(--sky-400)";
    case "spruce":
      return "var(--spruce-400)";
    case "ember":
      return "var(--coral-400)";
    case "proof":
      return "var(--proof-400)";
    default:
      return titleColorHex(item.metadata.colorHex) ?? "var(--gold-400)";
  }
}

function CustomiseDrawer({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [baseTheme, setBaseThemeState] = React.useState(storedBaseTheme());

  const inventoryQuery = useQuery({
    queryKey: ["store/inventory"],
    queryFn: getInventory,
  });

  const equipMutation = useMutation({
    mutationFn: (data: { slot: EquipSlot; itemIds: string[] }) => equipItems(data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["store/inventory"] }),
        queryClient.invalidateQueries({ queryKey: ["profile/stats"] }),
      ]);
    },
  });

  const equipped: EquippedIds = inventoryQuery.data?.equipped ?? {
    badgeItemIds: [],
    bannerItemId: null,
    titleItemId: null,
    mapThemeItemId: null,
    frameItemId: null,
    traceStyleItemId: null,
    stampItemId: null,
    pinItemId: null,
  };
  const items = inventoryQuery.data?.items ?? [];

  function toggleBadge(itemId: string) {
    const selected = equipped.badgeItemIds.includes(itemId);
    const next = selected
      ? equipped.badgeItemIds.filter((id) => id !== itemId)
      : equipped.badgeItemIds.length < 3
        ? [...equipped.badgeItemIds, itemId]
        : equipped.badgeItemIds;
    if (next === equipped.badgeItemIds) return;
    equipMutation.mutate({ slot: "badges", itemIds: next });
  }

  function toggleSingle(slot: SingleSlot, currentId: string | null, itemId: string) {
    const unequipping = currentId === itemId;
    equipMutation.mutate({ slot, itemIds: unequipping ? [] : [itemId] });
    if (slot === "mapTheme") {
      // Apply the site skin immediately rather than waiting for the
      // profile/stats refetch; AppShell settles the final state.
      const item = items.find((i) => i.itemId === itemId);
      const skin = item ? STORE_THEME_SKINS[item.slug] : undefined;
      applySiteTheme(unequipping ? baseTheme : (skin ?? baseTheme));
    }
  }

  const themeMutation = useMutation({
    mutationFn: (theme: string) => updateThemePreference(theme),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["profile/stats"] }),
  });

  function chooseFreeTheme(slug: string) {
    setBaseTheme(slug);
    setBaseThemeState(slug);
    applySiteTheme(slug);
    // Persist per profile (ATL-28) so other devices pick it up too.
    themeMutation.mutate(slug);
    // A free base theme replaces any equipped store skin.
    if (equipped.mapThemeItemId) {
      equipMutation.mutate({ slot: "mapTheme", itemIds: [] });
    }
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", justifyContent: "flex-end" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)" }} onClick={onClose} />
      <div
        style={{
          position: "relative",
          width: 440,
          maxWidth: "92vw",
          height: "100%",
          background: "var(--surface-card)",
          borderLeft: "1px solid var(--border-strong)",
          boxShadow: "var(--shadow-xl)",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 22px 14px",
            borderBottom: "1px solid var(--border-subtle)",
          }}
        >
          <div>
            <p className="eyebrow" style={{ fontSize: 10, marginBottom: 4 }}>Customise</p>
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)" }}>Your cosmetics</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: "16px 22px 28px", display: "flex", flexDirection: "column", gap: 24 }}>
          {inventoryQuery.isLoading &&
            [0, 1, 2].map((i) => <div key={i} style={{ height: 60, background: "var(--surface-sunken)", borderRadius: "var(--radius-md)" }} />)}

          {equipMutation.isError && (
            <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
              {errorMessage(equipMutation.error, "Failed to update")}
            </p>
          )}

          {!inventoryQuery.isLoading &&
            SLOT_GROUPS.map((group) => {
              const owned = items.filter((i) => i.kind === group.kind);
              const isBadges = group.slot === "badges";
              const currentSingleId =
                group.slot === "banner"
                  ? equipped.bannerItemId
                  : group.slot === "title"
                    ? equipped.titleItemId
                    : group.slot === "mapTheme"
                      ? equipped.mapThemeItemId
                      : group.slot === "frame"
                        ? equipped.frameItemId
                        : group.slot === "traceStyle"
                          ? equipped.traceStyleItemId
                          : group.slot === "stamp"
                            ? equipped.stampItemId
                            : group.slot === "pin"
                              ? equipped.pinItemId
                              : null;

              return (
                <div key={group.slot}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
                    <p className="eyebrow" style={{ fontSize: 10, margin: 0 }}>{group.label}</p>
                    {isBadges && (
                      <span className="tnum" style={{ fontSize: 11, color: "var(--text-muted)" }}>
                        {equipped.badgeItemIds.length === 3
                          ? "3 of 3 equipped"
                          : `${equipped.badgeItemIds.length} of 3 equipped`}
                      </span>
                    )}
                  </div>

                  {group.slot === "mapTheme" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 6 }}>
                      {FREE_THEMES.map((t) => {
                        const isSelected = !equipped.mapThemeItemId && baseTheme === t.slug;
                        return (
                          <button
                            key={t.slug}
                            type="button"
                            onClick={() => chooseFreeTheme(t.slug)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              padding: "8px 12px",
                              borderRadius: "var(--radius-md)",
                              border: `1px solid ${isSelected ? "var(--border-default)" : "var(--border-subtle)"}`,
                              background: isSelected ? "var(--surface-hover)" : "transparent",
                              cursor: "pointer",
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            <span
                              style={{
                                display: "flex",
                                width: 40,
                                height: 24,
                                borderRadius: "var(--radius-sm)",
                                overflow: "hidden",
                                flexShrink: 0,
                                border: "1px solid var(--border-subtle)",
                              }}
                            >
                              {t.swatch.map((c) => (
                                <span key={c} style={{ flex: 1, background: c }} />
                              ))}
                            </span>
                            <span style={{ flex: 1, fontSize: 13, color: "var(--text-primary)" }}>{t.name}</span>
                            {isSelected && <Check size={16} style={{ color: "var(--spruce-400)", flexShrink: 0 }} />}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {owned.length === 0 ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        padding: "12px 14px",
                        background: "var(--surface-sunken)",
                        borderRadius: "var(--radius-md)",
                      }}
                    >
                      <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                        You don't own any{" "}
                        {group.slot === "mapTheme"
                          ? "store themes"
                          : group.slot === "frame"
                            ? "avatar frames"
                            : group.slot === "traceStyle"
                              ? "trace styles"
                              : group.slot === "pin"
                                ? "map pins"
                                : group.slot === "stamp"
                                  ? "check-in stamps"
                                  : group.label.toLowerCase()}{" "}
                        yet.
                      </span>
                      <Link
                        to="/store"
                        style={{ fontSize: 12.5, color: "var(--sky-300)", textDecoration: "none", flexShrink: 0 }}
                      >
                        Browse the store
                      </Link>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {owned.map((item) => {
                        const isSelected = isBadges
                          ? equipped.badgeItemIds.includes(item.itemId)
                          : currentSingleId === item.itemId;
                        const disabled =
                          equipMutation.isPending ||
                          (isBadges && !isSelected && equipped.badgeItemIds.length >= group.max);
                        const glow = rarityGlowSm(item.rarity);
                        return (
                          <button
                            key={item.itemId}
                            type="button"
                            disabled={disabled}
                            onClick={() =>
                              isBadges
                                ? toggleBadge(item.itemId)
                                : toggleSingle(group.slot as SingleSlot, currentSingleId, item.itemId)
                            }
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              padding: "8px 12px",
                              borderRadius: "var(--radius-md)",
                              border: `1px solid ${isSelected ? "var(--border-default)" : "var(--border-subtle)"}`,
                              background: isSelected ? "var(--surface-hover)" : "transparent",
                              boxShadow: glow,
                              cursor: disabled ? "not-allowed" : "pointer",
                              opacity: disabled && !isSelected ? 0.5 : 1,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {item.kind === "title" ? (
                              // The title text IS the preview — full-width,
                              // styled in its colour token, no media box.
                              <span
                                className="eyebrow"
                                style={{
                                  flex: 1,
                                  fontSize: 12,
                                  color: titleColor(item),
                                  minWidth: 0,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  padding: "6px 0",
                                }}
                              >
                                {item.metadata.text ?? item.name}
                              </span>
                            ) : (
                              <>
                                <span
                                  style={{
                                    minWidth: 40,
                                    height: 40,
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "var(--radius-sm)",
                                    background: "var(--surface-sunken)",
                                    overflow: "hidden",
                                  }}
                                >
                                  {itemPreview(item)}
                                </span>
                                <span style={{ flex: 1, fontSize: 13, color: "var(--text-primary)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {item.name}
                                </span>
                              </>
                            )}
                            {item.rarity !== "common" && (
                              <span
                                className="eyebrow"
                                style={{ fontSize: 9, flexShrink: 0, color: rarityColor(item.rarity) }}
                              >
                                {rarityLabel(item.rarity)}
                              </span>
                            )}
                            {isSelected && <Check size={16} style={{ color: "var(--spruce-400)", flexShrink: 0 }} />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

// ─── ProfilePage ──────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showResetDialog, setShowResetDialog] = React.useState(false);
  // /profile#customise (announcement CTAs) lands with the drawer already open.
  const [showCustomise, setShowCustomise] = React.useState(
    () => typeof window !== "undefined" && window.location.hash === "#customise",
  );
  const [avatarUploading, setAvatarUploading] = React.useState(false);
  const [avatarError, setAvatarError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const profileStatsQuery = useQuery({
    queryKey: ["profile/stats"],
    queryFn: fetchProfileStats,
  });
  const equipped = profileStatsQuery.data?.equipped;
  const bannerSrc = getBannerAsset(equipped?.banner?.metadata.assetKey);

  const logoutMutation = useMutation({
    mutationFn: () => logout(),
    onSuccess: async () => {
      queryClient.clear();
      await navigate({ to: "/login" });
    },
  });

  async function handleAvatarUpload(file: File) {
    if (!file.type.startsWith("image/")) {
      setAvatarError("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError("Image must be under 5MB");
      return;
    }

    setAvatarUploading(true);
    setAvatarError(null);

    try {
      const { uploadUrl, publicUrl, cacheControl } = await apiPost<{
        uploadUrl: string;
        publicUrl: string;
        cacheControl: string | null;
      }>("/account/avatar/upload-url", { contentType: file.type, cacheControl: true }).catch(() => {
        throw new Error("Failed to get upload URL");
      });

      // Upload directly to S3. Cache-Control is part of the presigned
      // signature, so it must match what the API signed exactly.
      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
          ...(cacheControl ? { "Cache-Control": cacheControl } : {}),
        },
      });
      if (!uploadRes.ok) throw new Error("Upload to storage failed");

      // Update user record
      await apiPatch<{ user: unknown }>("/account/me", { avatarUrl: publicUrl });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    } catch (err) {
      setAvatarError(errorMessage(err, "Upload failed"));
    } finally {
      setAvatarUploading(false);
    }
  }

  const billingQuery = useQuery({
    queryKey: ["billing"],
    queryFn: fetchBillingStatus,
  });
  const billing = billingQuery.data;

  const planDescription = billing?.isPro
    ? "Pro · renews via Stripe portal"
    : `${user?.plan === "pro" ? "Pro" : "Free"} plan · Manage subscription`;

  const quickLinks = [
    {
      to: "/statistics",
      icon: <BarChart3 size={22} />,
      label: "Statistics",
      description: "Scores, lifetime stats & activity history",
    },
    {
      to: "/connected-sources",
      icon: <Plug size={22} />,
      label: "Connected Sources",
      description: "Strava, GPX files & data imports",
    },
    {
      to: "/plan",
      icon: <CreditCard size={22} />,
      label: "Plan & Billing",
      description: planDescription,
    },
  ];

  return (
    <div
      style={{
        overflow: "auto",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Profile header ─────────────────────────────────────────────── */}
      <div
        className={bannerSrc ? undefined : "atlas-topo"}
        style={{
          position: "relative",
          // With a banner equipped the header grows so the artwork reads as a
          // banner (assets are 1200×280) instead of a cropped dark sliver.
          padding: bannerSrc ? "96px 28px 24px" : "32px 28px 28px",
          // Clearance so the rarity drop glow fades into the background
          // instead of painting over the cards below.
          marginBottom: 24,
          ...(bannerSrc
            ? {
                backgroundImage: `url("${bannerSrc}")`,
                backgroundSize: "cover",
                // Banner art keeps its horizon/detail in the bottom third —
                // a centered crop on a wide, short header shows only dark sky.
                backgroundPosition: "center bottom",
                boxShadow: rarityGlowMd(equipped?.banner?.rarity),
              }
            : {}),
        }}
      >
        {/* Scrim: with a banner, keep the top unobscured and only protect the
            text zone at the bottom; without one, keep the original fade. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background: bannerSrc
              ? "linear-gradient(0deg, color-mix(in srgb, var(--bg-app) 82%, transparent) 0%, color-mix(in srgb, var(--bg-app) 35%, transparent) 38%, transparent 68%)"
              : "linear-gradient(0deg, var(--bg-app) 0%, color-mix(in srgb, var(--bg-app) 40%, transparent) 100%)",
          }}
        />
        <div
          className="r-wrap"
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 20,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleAvatarUpload(file);
              e.target.value = "";
            }}
          />
          <div className="r-basis-full" style={{ flex: 1, minWidth: 0 }}>
            <ProfileIdentity
              name={user?.name ?? "Explorer"}
              title={equipped?.title}
              badges={equipped?.badges}
              frame={equipped?.frame}
              size="lg"
              avatarSlot={
                <ProfileAvatar
                  name={user?.name ?? ""}
                  avatarUrl={user?.avatarUrl}
                  size={72}
                  uploading={avatarUploading}
                  onUploadClick={() => fileInputRef.current?.click()}
                />
              }
              subtitle={
                user?.email ? (
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>
                    {user.email}
                  </div>
                ) : undefined
              }
            />
            {avatarError && (
              <div style={{ fontSize: 12, color: "var(--status-danger)", marginTop: 4 }}>
                {avatarError}
              </div>
            )}
          </div>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Sparkles size={14} />}
            onClick={() => setShowCustomise(true)}
          >
            Customise
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<LogOut size={14} />}
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
          >
            Sign out
          </Button>
        </div>
      </div>

      <div className="r-page-x" style={{ padding: "0 28px 28px", display: "flex", flexDirection: "column", gap: 20 }}>
        {/* ── Quick nav ────────────────────────────────────────────────── */}
        <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          {quickLinks.map(({ to, icon, label, description }) => (
            <Link
              key={to}
              to={to}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "16px 18px",
                background: "var(--surface-card)",
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-lg)",
                boxShadow: "var(--shadow-sm), var(--ring-top)",
                textDecoration: "none",
                color: "var(--text-primary)",
                transition: "border-color 0.15s ease, background 0.15s ease",
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.currentTarget.style.borderColor = "var(--border-gold)";
                e.currentTarget.style.background = "var(--accent-soft)";
              }}
              onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.currentTarget.style.borderColor = "var(--border-subtle)";
                e.currentTarget.style.background = "var(--surface-card)";
              }}
            >
              <span
                style={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: "var(--radius-md)",
                  background: "var(--surface-raised)",
                  border: "1px solid var(--border-default)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--text-accent)",
                }}
              >
                {icon}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{label}</div>
                <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>{description}</div>
              </div>
              <ChevronRight size={16} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
            </Link>
          ))}
        </div>

        {/* ── Add / verify email (renders nothing once contactable) ────── */}
        <EmailCaptureCard />

        {/* ── Sign-in methods (ATL-130) ─────────────────────────────────── */}
        {user && <SignInMethodsPanel />}

        {/* ── Security ─────────────────────────────────────────────────── */}
        {user && (
          <div id="security" style={{ scrollMarginTop: 24 }}>
            <SecurityPanel hasPassword={user.hasPassword} />
          </div>
        )}

        {/* ── Public profile ───────────────────────────────────────────── */}
        {user && (
          <div id="public-profile" style={{ scrollMarginTop: 24 }}>
            <PublicProfilePanel user={user} />
          </div>
        )}

        {/* ── Your invites (spec 21) ───────────────────────────────────── */}
        {user && <InvitesPanel />}

        {/* ── Your feedback (threads with our replies) ─────────────────── */}
        <AtlasPanel
          title="Your feedback"
          action={
            <Link to="/feedback" style={{ fontSize: 13, color: "var(--text-accent)", textDecoration: "none" }}>
              View all
            </Link>
          }
        >
          <p style={{ fontSize: 13.5, color: "var(--text-secondary)", margin: 0, lineHeight: 1.5 }}>
            Bug reports, ideas and missing data you have sent us, with our replies. Replies also
            reach you by email and in the notification bell.
          </p>
        </AtlasPanel>

        {/* ── Email preferences (spec 16) ──────────────────────────────── */}
        {user && <EmailPreferencesPanel />}

        {/* ── Announcements archive ────────────────────────────────────── */}
        <AnnouncementsPanel />

        {/* ── Your data (spec 17) ──────────────────────────────────────── */}
        <DataExportPanel />

        {/* ── Danger zone ──────────────────────────────────────────────── */}
        <AtlasPanel title="Danger zone">
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>
                  Reset account
                </div>
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                  Wipe all activities, achievements, waypoints, Outdoor Score and posts. Keeps your plan and connected sources.
                </div>
              </div>
              <Button
                variant="danger"
                size="sm"
                leftIcon={<RotateCcw size={14} />}
                onClick={() => setShowResetDialog(true)}
              >
                Reset account
              </Button>
            </div>

            <div style={{ height: 1, background: "var(--border-subtle)" }} />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>
                  Delete account
                </div>
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                  Permanently remove your account and all activity data
                </div>
              </div>
              <Button
                variant="danger"
                size="sm"
                leftIcon={<Trash2 size={14} />}
                onClick={() => setShowDeleteDialog(true)}
              >
                Delete account
              </Button>
            </div>
          </div>
        </AtlasPanel>
      </div>

      {showDeleteDialog && (
        <DeleteAccountDialog onClose={() => setShowDeleteDialog(false)} />
      )}
      {showResetDialog && (
        <ResetAccountDialog onClose={() => setShowResetDialog(false)} />
      )}
      {showCustomise && <CustomiseDrawer onClose={() => setShowCustomise(false)} />}
    </div>
  );
}
